import { useNavigate } from 'react-router-dom'
import { IProjectStatus } from '~/@api/generated-api-types-project/Api'
import { ITranslatedText } from '~/@pages_investing/projects/_project_types/project.types'
import { cn } from '~/shadcn/lib/utils'

import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import type { ProjectWithSchemAndExtraYieldSettings } from '~/@pages_investing/projects/_projects_ui/project-yield-text/ProjectYieldText'
import { InvestButton } from '~/@pages_investing/projects/_projects_ui/invest-button/InvestButton'
import type { ProjectSchema } from '../../projects/_project_types/project.types'
import { getMapCoordinates } from '../project-detail_ui/established-property-info/utils'
import { EstablishedPropertyInfo } from '../project-detail_ui/established-property-info/EstablishedPropertyInfo'
import { InvestmentSecurity } from '../project-detail_ui/investment-security/InvestmentSecurity'
import { DebtorInfo } from '../project-detail_ui/debtor-info/DebtorInfo'
import { isStageFullyFunded } from '../project_utils/project.utils'
import { ProjectAttachments } from '../project-detail_ui/project-attachments/ProjectAttachments'
import { RunningProjectV2Overview } from './ProjectV2Overview_Running'
import { FinishedProjectV2Overview } from './ProjectV2Overview_Finished'
import { ProjectV2Description } from './ProjectV2Description'
import { ProjectV2Parameters } from './ProjectV2Parameters'

type Props = {
  project: ProjectSchema
}

function ProjectV2Detail({ project }: Props) {
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()

  const coordinates = getMapCoordinates(
    project.collateralInfo.propertyInfo?.mapCoordinates
  )

  const isInvestButtonDisabled = isStageFullyFunded(project)
  const isRunningProject = project.statusId === IProjectStatus.Running

  const handleInvestClick = () => {
    navigate(`/order/${project.businessId}/${project.stageNumber}`)
  }

  const content = (() => {
    if (isDesktop) {
      return (
        <div>
          <div className="flex flex-row items-start">
            <div
              className={cn(
                'flex-1 max-w-[520px]',
                'sticky top-0',
                'h-full md:h-screen',
                'pb-10',
                'bg-foreground',
                'border-r border-border'
              )}>
              <ProjectV2Parameters project={project} />
              {isRunningProject && isDesktop && (
                <div className="w-full flex justify-center items-center bg-[#121212]">
                  <InvestButton
                    disabled={isInvestButtonDisabled}
                    onInvest={handleInvestClick}
                    className="w-[330px] px-5 lg:px-[60px]"
                  />
                </div>
              )}
            </div>

            <div className="flex-1">
              <Details project={project} />
            </div>
          </div>
        </div>
      )
    }

    return (
      <>
        <ProjectV2Parameters project={project} />
        <Details project={project} />
        {isRunningProject && isDesktop && (
          <div className="flex justify-center p-5">
            <InvestButton
              disabled={isInvestButtonDisabled}
              onInvest={handleInvestClick}
            />
          </div>
        )}
      </>
    )
  })()

  const projectOverview = (() => {
    if (isRunningProject) {
      return (
        <RunningProjectV2Overview
          project={project as unknown as ProjectWithSchemAndExtraYieldSettings}
          isInvestButtonDisabled={isInvestButtonDisabled}
          onInvestClick={handleInvestClick}
        />
      )
    }
    return <FinishedProjectV2Overview project={project} />
  })()

  return (
    <div className="w-full">
      {projectOverview}
      {project.expertReview && (
        <ProjectV2Description
          project={project}
          descriptionContent={{
            [ITranslatedText.Sk]:
              (
                project.expertReview as unknown as Record<
                  ITranslatedText,
                  string
                >
              )?.[ITranslatedText.Sk] ?? '',
          }}
          coordinates={coordinates}
        />
      )}
      {content}
    </div>
  )
}

type DetailsProps = {
  project: ProjectSchema
}

function Details({ project }: DetailsProps) {
  return (
    <>
      <EstablishedPropertyInfo project={project} />
      <InvestmentSecurity project={project} />
      <ProjectAttachments project={project} />
      <DebtorInfo project={project} />
    </>
  )
}

export { ProjectV2Detail }
